import { useCallback, useEffect, useState } from "react";
import axios from "../../../axios";
import TopPageTitle from "../../../components/TopPageTitle/TopPageTitle";
import Loader from "../../../components/Loader/Loader";
import { PagesWrapper } from "../../../styles/components/PagesWrapper.styled";
import ShopItemEditor from "./ShopItemEditor";
import * as styled from "./AdminShop.styled";

const STATUS_FILTERS = [
  { value: "new", label: "Нові" },
  { value: "done", label: "Видані" },
  { value: "cancelled", label: "Скасовані" },
  { value: "", label: "Усі" },
];

const STATUS_LABELS = { new: "Нове", done: "Видано", cancelled: "Скасовано" };

const EMPTY_ITEM = { title: "", description: "", priceStars: 10, kind: "other", limitPerOwner: 0, active: true, sortOrder: 10 };

function AdminShop() {
  const [tab, setTab] = useState("orders");
  const [status, setStatus] = useState("new");
  const [orders, setOrders] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get("shop/admin/orders", { params: status ? { status } : {} });
      setOrders(data);
    } catch (loadError) {
      setError("Не вдалося завантажити замовлення");
    } finally {
      setLoading(false);
    }
  }, [status]);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get("shop/admin/items");
      setItems(data);
    } catch (loadError) {
      setError("Не вдалося завантажити товари");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setError(null);
    if (tab === "orders") loadOrders();
    else loadItems();
  }, [tab, loadOrders, loadItems]);

  const runAction = async (request, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setError(null);
    try {
      await request();
      await loadOrders();
    } catch (actionError) {
      setError(actionError.response?.data?.message || "Не вдалося виконати дію");
    }
  };

  const saveItem = async (item) => {
    const { _id, ...fields } = item;
    if (_id) await axios.patch(`shop/admin/items/${_id}`, fields);
    else await axios.post("shop/admin/items", fields);
    await loadItems();
  };

  const deleteItem = async (id) => {
    await axios.delete(`shop/admin/items/${id}`);
    await loadItems();
  };

  return (
    <PagesWrapper>
      <TopPageTitle value={"Магазин зірок"} />

      <styled.Tabs>
        <styled.Tab $active={tab === "orders"} onClick={() => setTab("orders")}>
          Замовлення
        </styled.Tab>
        <styled.Tab $active={tab === "items"} onClick={() => setTab("items")}>
          Товари
        </styled.Tab>
      </styled.Tabs>

      {error && <styled.Error>{error}</styled.Error>}

      {tab === "orders" && (
        <>
          <styled.Filters>
            {STATUS_FILTERS.map(({ value, label }) => (
              <styled.Filter key={label} $active={status === value} onClick={() => setStatus(value)}>
                {label}
              </styled.Filter>
            ))}
          </styled.Filters>

          {loading ? (
            <Loader />
          ) : orders.length === 0 ? (
            <styled.Empty>Замовлень немає</styled.Empty>
          ) : (
            <styled.List>
              {orders.map((order) => (
                <styled.Row key={order._id}>
                  <styled.RowMain>
                    <styled.RowTitle>
                      {order.title} · {order.priceStars} ⭐
                    </styled.RowTitle>
                    <styled.RowMeta>
                      {new Date(order.createdAt).toLocaleString("uk-UA")} · учень: {order.ownerName}
                    </styled.RowMeta>
                    {order.friendName && (
                      <styled.RowMeta>
                        🎁 друг: {order.friendName}, {order.friendPhone}
                        {order.referralRewarded && " · бонус +20 ⭐ нараховано"}
                      </styled.RowMeta>
                    )}
                  </styled.RowMain>

                  <styled.Actions>
                    <styled.StatusBadge $status={order.status}>{STATUS_LABELS[order.status]}</styled.StatusBadge>
                    {order.status === "new" && (
                      <>
                        <styled.Button
                          onClick={() => runAction(() => axios.patch(`shop/admin/orders/${order._id}`, { status: "done" }))}
                        >
                          Видано
                        </styled.Button>
                        <styled.GhostButton
                          onClick={() =>
                            runAction(
                              () => axios.patch(`shop/admin/orders/${order._id}`, { status: "cancelled" }),
                              `Скасувати замовлення? Учню повернуться ${order.priceStars} ⭐.`
                            )
                          }
                        >
                          Скасувати
                        </styled.GhostButton>
                      </>
                    )}
                    {order.kind === "gift_friend" && order.status !== "cancelled" && !order.referralRewarded && (
                      <styled.GhostButton
                        onClick={() =>
                          runAction(
                            () => axios.post(`shop/admin/orders/${order._id}/referral-bonus`),
                            `${order.friendName} купив(ла) пакет? Учню ${order.ownerName} буде нараховано +20 ⭐.`
                          )
                        }
                      >
                        Друг купив пакет
                      </styled.GhostButton>
                    )}
                  </styled.Actions>
                </styled.Row>
              ))}
            </styled.List>
          )}
        </>
      )}

      {tab === "items" &&
        (loading ? (
          <Loader />
        ) : (
          <styled.List>
            {items.map((item) => (
              <ShopItemEditor key={item._id} item={item} onSave={saveItem} onDelete={deleteItem} />
            ))}
            <ShopItemEditor item={EMPTY_ITEM} onSave={saveItem} isNew />
          </styled.List>
        ))}
    </PagesWrapper>
  );
}

export default AdminShop;
